import {type ExportTarget} from "@khan/khanmigo-coediting";

/**
 * Metadata and frontend configuration for a Khanmigo tool.
 * Each tool must have a unique `urlSlug`, and must have a `configName` that
 * points to a backend config containing the tool's prompts and settings.
 * Other fields here are used to configure the frontend experience of the tool.
 */
export interface Tool {
    /**
     * The URL slug for the tool. This is used to generate the URL for the tool.
     */
    urlSlug: string;
    /**
     * The name of the backend prompt config for the tool.
     */
    configName: string;
    /**
     * The title of the tool.
     */
    title: () => string;
    /**
     * The description of the tool.
     */
    description: () => string;
    /**
     * The footer of the tool.
     */
    footer?: () => string;
    /**
     * The categories of the tool. Used for filtering and grouping on the
     * list-of-tools page.
     */
    categories: ToolCategories[];
    /**
     * The icon we use to represent the tool.
     */
    icon: PhosphorRegular;
    /**
     * The color of the tool, used for the background of the tool icon and
     * as an accent color when necessary.
     */
    color: string;
    /**
     * The inputs that the tool requires from the user. An empty array means
     * no inputs are needed for this tool.
     */
    requestedInputs: ToolRequestedInput[];
    /**
     * All ways that this tool can be exported.
     */
    exportTargets?: ExportTarget[];
    /**
     * Defines how the tool should be rendered within the embedding app.
     */
    component: React.ComponentType<ToolComponentProps>;

    // TODO(reid): Do we need the below fields anymore in Classroom?
    layout: Layout;
    extensionOrder?: number;
    activitySlug?: string;
    reportingLabelId: string;
}

export interface ToolRequestedInput {
    label: () => string;
    description: () => string;
    variableName: string;
    initialValue?: string;
    required?: boolean;
    inputKind:
        | "TEXT_BOX"
        | "TEXT_AREA"
        | "SINGLE_SELECT"
        | "GRID_SELECT"
        | "CLASSROOM_SELECT"
        | "CONTENT_SEARCH"
        | "DYNAMIC_TEXT_FIELDS";
    /**
     * For input kinds that allow selecting from an array of options, like
     * SINGLE_SELECT and GRID_SELECT, this is how we define the available
     * options to select from. This may be either a static list or a function
     * that returns a list of options, based on the current input form values.
     * The latter allows for a dynamic set of options based on other fields.
     */
    selectOptions?:
        | RequestedInputSelectOption[]
        | ((values: Record<string, string>) => RequestedInputSelectOption[]);
    maxCharacters?: number;
    maxFields?: number;
    /**
     * If provided, this input can be dynamically hidden or shown based on the
     * current form input values. If this isn't provided, the field will always
     * be shown.
     */
    show?: (values: Record<string, string>) => boolean;
}

interface RequestedInputSelectOption {
    value: string;
    label: () => string;
    description?: () => string;
    tooltip?: () => string;
}

/**
 * Props that are passed to the component that renders each tool. See
 * the `component` field in the `Tool` interface. Not every tool may need
 * all of these props, but they are all passed in for consistency and
 * convenience.
 */
export interface ToolComponentProps {
    configName: string;
    urlSlug: string;
    title: string;
    description: string;
    footer?: string;
    providedInputs: Record<string, string>;
    persona: ToolPersona;
    debugMode?: boolean;
}

/**
 * Specifies which layout a particular teacher tool should present.
 * Previously we assumed that if chatOnly was false we'd use the coeditor, but
 * with Blooket can no longer make that assumption.
 */
export enum Layout {
    Blooket = "Blooket",
    ChatOnly = "ChatOnly",
    Coeditor = "Coeditor",
    EditAssignment = "EditAssignment",
}

export enum Platform {
    KhanAcademy = "KhanAcademy",
    Canvas = "Canvas",
}

export enum ToolCategories {
    All = "All Tools",
    Favorites = "My Favorites",
    Plan = "Plan",
    Create = "Create",
    Differentiate = "Differentiate",
    Support = "Support",
    Learn = "Learn",
}

// sync-start:tool-persona 531486882 https://github.com/Khan/webapp/blob/master/libs/guide-core-helpers/src/use-ai-guide-toolkit-persona.ts
export enum ToolPersona {
    Teacher = "TEACHER",
    Parent = "PARENT",
    Admin = "ADMIN",
}
// sync-end:tool-persona

export type AutosavingStatus = "idle" | "loading" | "saved" | "error";
